import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_FEDEX_PACKAGING_ID,
  FEDEX_INTERNATIONAL_PACKAGING,
  FEDEX_ONE_RATE_PACKAGING,
  getFedExPackagingOption,
  type FedExPackagingId,
} from './fedexPackagingCatalog';

type Props = {
  value: FedExPackagingId;
  onChange: (next: FedExPackagingId) => void;
  isInternational?: boolean;
  disabled?: boolean;
  compact?: boolean;
};

export default function FedExPackagingPicker({
  value,
  onChange,
  isInternational = false,
  disabled = false,
  compact = false,
}: Props) {
  const { t } = useTranslation();
  const selected = getFedExPackagingOption(value) || getFedExPackagingOption(DEFAULT_FEDEX_PACKAGING_ID);

  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      <label className="text-[10px] font-medium text-slate-600">
        {t('backoffice.carrier_fedex_box', { defaultValue: 'FedEx box' })}
        <select
          className="mt-0.5 w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-xs disabled:bg-slate-50"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value as FedExPackagingId)}
        >
          <optgroup
            label={t('backoffice.carrier_fedex_one_rate', {
              defaultValue: 'One Rate (domestic express)',
            })}
          >
            {FEDEX_ONE_RATE_PACKAGING.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label} — {opt.dimensionsLabel}
              </option>
            ))}
          </optgroup>
          {isInternational ? (
            <optgroup
              label={t('backoffice.carrier_fedex_intl_boxes', {
                defaultValue: 'International fixed-weight boxes',
              })}
            >
              {FEDEX_INTERNATIONAL_PACKAGING.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} — {opt.dimensionsLabel}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
      </label>
      {selected ? (
        <p className="text-[10px] leading-snug text-slate-500">
          {selected.hint}
          {selected.maxWeightLbs > 0 ? ` · max ${selected.maxWeightLbs} lb` : ''}
        </p>
      ) : null}
    </div>
  );
}
